/**
 * Putting an assembled, layered, tinted hero in the dungeon -- from a built pack.
 *
 * **The manifest is the only asset index.** One URL is named here, the pack's own
 * location; every mesh, clip, atlas and mask URL comes out of `pack.manifest.json`.
 * Nothing in this package spells out a part, clip or texture path, which is what
 * keeps art iteration out of the client's source: re-curate, rebuild the pack, and
 * the client follows without a code change.
 *
 * Animation bundles are fetched per stance, so a swordsman-only session never
 * downloads the archer's clips.
 */
import {
  AnimationLayerRig,
  StanceController,
  assembleHero,
  attachWeapon,
  createHeroTintMaterial,
  loadSharedTintPalette,
  resolveStanceClips,
  type AssembledHero,
  type AttachedWeapon,
  type HeroTintMaterial,
  type TintSelection,
} from '@dungeon-builder/character-kit';
import {
  readPackManifest,
  type Gender,
  type ManifestBundle,
  type ManifestClip,
  type ManifestSlot,
  type ManifestVariant,
  type ManifestWeapon,
  type PackManifest,
  type StanceClass,
} from '@dungeon-builder/shared';
import type { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

/** The one path this module names. Everything else comes from the manifest. */
const PACK = '/assets/packs/starter';

/**
 * The manifest's shape, its version and its migrations live in
 * `@dungeon-builder/shared/character/manifest.ts` -- the pipeline writes it, this
 * reads it and the budget report measures it, so one declaration serves all three.
 */
export type {
  ManifestBundle,
  ManifestClip,
  ManifestSlot,
  ManifestVariant,
  ManifestWeapon,
  PackManifest,
};

let manifestPromise: Promise<PackManifest> | undefined;

export function packManifest(): Promise<PackManifest> {
  const url = `${PACK}/pack.manifest.json`;
  manifestPromise ??= fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `${response.status} fetching the pack manifest — has \`npm run pipeline -- pack starter\` run?`,
        );
      }
      return response.json() as Promise<unknown>;
    })
    // A pack outlives the client that reads it, so the version is checked and
    // migrated here rather than assumed. `readPackManifest` throws a message naming
    // which side is behind.
    .then((raw) => readPackManifest(raw, url));
  return manifestPromise;
}

const packUrl = (relative: string): string => `${PACK}/${relative}`;

export type MovementState = 'idle' | 'walk' | 'run' | 'sprint';

export interface AvatarOptions {
  gender: Gender;
  position?: Vector3;
  /** Which combat bundle to fetch. Appearance is unaffected. */
  stance?: StanceClass;
  colourway?: string;
  tints?: TintSelection;
  /**
   * Exact catalogue variant ids to wear. This is how a peer is reproduced: the ids
   * come over the wire, so two clients render the same character from the same pack
   * without exchanging any geometry. Omitted, the pack's defaults are used.
   */
  variantIds?: string[];
  /** Weapon id from the manifest. Omitted, the stance's first suitable one is used. */
  weaponId?: string;
}

export interface Avatar extends AssembledHero {
  gender: Gender;
  stance: StanceClass | undefined;
  triangles: number;
  rig: AnimationLayerRig;
  tint: HeroTintMaterial;
  /** Null when the pack enables no weapon for this stance. */
  weapon: AttachedWeapon | null;
  /** Null when no stance was requested. */
  controller: StanceController | null;
  move(state: MovementState, blendMs?: number): void;
}

export async function spawnAvatar(scene: Scene, options: AvatarOptions): Promise<Avatar> {
  const manifest = await packManifest();
  const cut = options.gender === 'male' ? 'masc' : 'femn';

  // One part per slot: the ids the caller asked for, else the variant the pack marks
  // default, else the lowest index this gender can wear.
  const wanted = new Set(options.variantIds ?? []);
  const parts = manifest.slots.flatMap((slot) => {
    const wearable = slot.variants.filter(
      (variant) => variant.scope === 'shared' || variant.scope === options.gender,
    );
    const requested = wearable.find((variant) => wanted.has(variant.id));
    const chosen =
      requested ??
      [...wearable].sort((a, b) => Number(b.default) - Number(a.default) || a.index - b.index)[0];
    return chosen
      ? [{ slot: slot.slot, url: packUrl(slot.url), meshName: chosen.mesh }]
      : [];
  });
  if (parts.length === 0) throw new Error('the pack enables no wearable parts');

  const colourway =
    manifest.colourways.find((entry) => entry.id === options.colourway) ?? manifest.colourways[0];
  if (!colourway) throw new Error('the pack enables no colourway');

  // Shared across characters: a palette is decoded pixels, and the mask scan that
  // builds it does not vary by character.
  const palette = await loadSharedTintPalette(
    packUrl(colourway.url),
    manifest.tintRegions.map((region) => ({
      id: region.id,
      mask: packUrl(region.url),
      channel: region.channel,
    })),
  );
  const tint = createHeroTintMaterial(scene, palette, options.tints ?? {});

  const hero = await assembleHero(scene, parts, { material: tint.material });
  hero.root.position = options.position ?? Vector3.Zero();

  // Shared bundles, plus this stance's own. The other stance's bundle is never named,
  // so it is never fetched.
  const bundles = manifest.bundles.filter(
    (bundle) => bundle.shared || (options.stance !== undefined && bundle.stance === options.stance),
  );

  const rig = new AnimationLayerRig(scene, hero.skeleton);
  let registered = 0;
  for (const bundle of bundles) {
    // One call per bundle, not per clip: the file is parsed once.
    //
    // The role is the clip's authored one because that is how this character drives
    // it: locomotion holds the lower layer at all times, so the stance's combat clips
    // are composed onto the torso and the reactions take the whole body. A clip that
    // later needs a second role -- the same swing played whole, standing still -- is
    // registered again under it; the rig keys bindings by clip *and* role, so the two
    // coexist instead of the first one winning.
    registered += await rig.registerBundle(
      packUrl(bundle.url),
      bundle.clips
        .filter((clip) => clip.gender === 'neutral' || clip.gender === cut)
        .map((clip) => ({ id: clip.id, role: clip.layer })),
    );
  }

  const move = (state: MovementState, blendMs?: number): void => {
    const id = state === 'idle' ? `movement/idle/standing_${cut}` : `movement/${state}/f_${cut}`;
    if (rig.has(id, 'lower')) rig.play('lower', id, { blendMs });
  };

  move('idle');

  // --- weapon and stance (steps 6.4, 6.5) ---
  let weapon: AttachedWeapon | null = null;
  let controller: StanceController | null = null;

  if (options.stance) {
    // The manifest says which stances may carry each weapon (pack format v2), so
    // adding a third stance is a curation change rather than a change here.
    const stance = options.stance;
    const suitable = manifest.weapons.filter((entry) => entry.stances.includes(stance));
    const chosen =
      suitable.find((entry) => entry.id === options.weaponId) ?? suitable[0] ?? null;

    if (chosen) {
      weapon = await attachWeapon(scene, hero.skeleton, {
        socket: chosen.socket,
        url: packUrl(chosen.url),
        // Only the prop clips whose body clip this character registered: a bow flexes
        // in step with the body animation, and a clip the body never plays is dead
        // weight.
        propClips: Object.fromEntries(
          Object.entries(chosen.propClips ?? {})
            .filter(([bodyClip]) => rig.has(bodyClip))
            .map(([bodyClip, url]) => [bodyClip, packUrl(url)]),
        ),
      });
    }

    const available = bundles.flatMap((bundle) => bundle.clips.map((clip) => clip.id));
    controller = new StanceController({
      stance: options.stance,
      rig,
      clips: resolveStanceClips(options.stance, available.filter((id) => rig.has(id))),
      weapon,
    });
    controller.rest();
  }

  const triangles = hero.body.getTotalIndices() / 3;
  console.log(
    `[character] ${options.gender} ${options.stance ?? 'unarmed'}: ${triangles} triangles, ` +
      `${parts.length} slots, ${registered} clips from ${bundles.length} bundle(s), ` +
      `colourway ${colourway.id}${weapon ? `, weapon attached (${weapon.propClips.size} prop clips)` : ''}`,
  );

  return {
    ...hero,
    gender: options.gender,
    stance: options.stance,
    triangles,
    rig,
    tint,
    weapon,
    controller,
    move,
    dispose() {
      controller?.dispose();
      rig.dispose();
      hero.dispose();
      tint.dispose();
    },
  };
}
